import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Alert,
  Link
} from '@mui/material';
import { AutoAwesome as AgentIcon, Close as CloseIcon } from '@mui/icons-material';
import AgentCreationChatDialog from '../components/admin/AgentCreationChatDialog';
import { novidadesApi, Novidade } from '../utils/novidadesApi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const AdminLandingPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [description, setDescription] = useState('');
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [bannerNovidade, setBannerNovidade] = useState<Novidade | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    const loadBanner = async () => {
      try {
        const banner = await novidadesApi.getActiveBanner();
        if (banner) {
          if (banner.bannerExpirationDate) {
            const expirationDate = new Date(banner.bannerExpirationDate);
            const now = new Date();
            if (expirationDate > now) {
              setBannerNovidade(banner);
            }
          } else {
            setBannerNovidade(banner);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar banner:', error);
      }
    };

    loadBanner();
  }, []);

  const handleCreateAgent = () => {
    if (description.trim()) {
      setChatDialogOpen(true);
    }
  };

  const handleBannerDismiss = () => {
    setBannerDismissed(true);
  };

  const handleBannerClick = () => {
    navigate('/admin/novidades');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Banner de Novidade */}
      {bannerNovidade && !bannerDismissed && (
        <Box sx={{ mb: 5, mt: -5 }}>
          <Alert
            severity="info"
            sx={{
              position: 'relative',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(33, 150, 243, 0.2)' 
                  : 'rgba(33, 150, 243, 0.1)',
              },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBannerDismiss();
                }}
                sx={{ minWidth: 'auto', p: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </Button>
            }
            onClick={handleBannerClick}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {bannerNovidade.titulo}
              </Typography>
              {bannerNovidade.descricao && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {bannerNovidade.descricao}
                </Typography>
              )}
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBannerClick();
                }}
                sx={{
                  textDecoration: 'underline',
                  fontWeight: 500,
                }}
              >
                {t('adminLanding.seeMoreDetails')}
              </Link>
            </Box>
          </Alert>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          minHeight: '60vh',
          justifyContent: 'center'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 3,
            maxWidth: 800,
            width: '100%',
            background: theme.palette.mode === 'dark'
              ? '#1a1a1a'
              : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255,255,255,0.06)'
              : '1px solid rgba(0,0,0,0.1)'
          }}
        >
          <Box sx={{ mb: 4 }}>
            <AgentIcon
              sx={{
                fontSize: { xs: 48, md: 64 },
                color: 'primary.main',
                mb: 2
              }}
            />
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              sx={{
                fontWeight: 'bold',
                color: 'text.primary',
                mb: 2,
                lineHeight: 1.2
              }}
            >
              {t('adminLanding.title')}<br />
              {t('adminLanding.subtitle')}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
            >
              {t('adminLanding.description')} <br /> {t('adminLanding.descriptionLine2')}
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              label={t('adminLanding.inputLabel')}
              placeholder={t('adminLanding.inputPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' ? '#313131' : 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'primary.main',
                },
              }}
            />
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={handleCreateAgent}
            disabled={!description.trim()}
            startIcon={<AgentIcon />}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
              color: theme.palette.mode === 'dark' ? '#000' : undefined,
              textTransform: 'none',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #65f0c5 0%, #65f0c5 100%)'
                : undefined,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0,0,0,0.4)'
                : '0 4px 20px rgba(0,0,0,0.15)',
              '&:hover': {
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 6px 25px rgba(0,0,0,0.5)'
                  : '0 6px 25px rgba(0,0,0,0.2)',
                transform: 'translateY(-2px)',
                color: theme.palette.mode === 'dark' ? '#000' : undefined,
              },
              '&.Mui-disabled': {
                color: theme.palette.mode === 'dark' ? '#666' : undefined,
              },
              transition: 'all 0.3s ease',
            }}
          >
            {t('adminLanding.createAgent')}
          </Button>
        </Paper>
      </Box>

      <AgentCreationChatDialog
        open={chatDialogOpen}
        onClose={() => setChatDialogOpen(false)}
        initialDescription={description}
        onAgentCreated={() => {
          setChatDialogOpen(false);
          setDescription('');
        }}
      />
    </Container>
  );
};

export default AdminLandingPage;
